// The appearance (F467, decision 090): the reader's choice among following
// the system, light and dark, kept on this device (the browser's storage,
// never the platform: a display preference, like the text size of
// decision 071) and applied as `data-theme` on the root before the first
// paint (main.tsx), where the stylesheet redefines every token. "System"
// leaves the attribute off, so the stylesheet's own media query decides and
// follows the system as it changes. Inside the companion the window's own
// theme (the title bar) follows the same choice through the host.

import { useSyncExternalStore } from "react";

export type Theme = "system" | "light" | "dark";
export type Resolved = "light" | "dark";

export const THEMES: { value: Theme; label: string; hint: string }[] = [
  { value: "system", label: "Follow the system", hint: "Light or dark as Windows is set." },
  { value: "light", label: "Light", hint: "The pale workspace." },
  { value: "dark", label: "Dark", hint: "A navy workspace, easier at night." },
];

const KEY = "sharifly.theme";
const QUERY = "(prefers-color-scheme: dark)";
const listeners = new Set<() => void>();
let current: Theme = "system";

export function readTheme(): Theme {
  try {
    const v = window.localStorage.getItem(KEY);
    return THEMES.some((t) => t.value === v) ? (v as Theme) : "system";
  } catch {
    return "system";
  }
}

/** True when the system asks for dark; false where the browser cannot say. */
export function systemDark(): boolean {
  return typeof window !== "undefined" && typeof window.matchMedia === "function" ? !!window.matchMedia(QUERY).matches : false;
}

/** What the page shows for a choice: the choice itself, or the system's answer. */
export function resolveTheme(theme: Theme): Resolved {
  if (theme === "system") return systemDark() ? "dark" : "light";
  return theme;
}

/** Applies a choice to the document and keeps it for the next visit. */
export function applyTheme(theme: Theme): void {
  current = theme;
  if (theme === "system") delete document.documentElement.dataset.theme;
  else document.documentElement.dataset.theme = theme;
  try {
    window.localStorage.setItem(KEY, theme);
  } catch {
    // A private window or blocked storage: the choice holds for this visit.
  }
  for (const l of listeners) l();
}

export function currentTheme(): Theme {
  return current;
}

/** Tells `onChange` when the system's answer changes (the reader following it); returns the way to stop. */
export function watchSystemTheme(onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return () => undefined;
  const media = window.matchMedia(QUERY);
  media.addEventListener?.("change", onChange);
  return () => media.removeEventListener?.("change", onChange);
}

// The store's subscribers hear the system too (a map following the system
// swaps its tiles when Windows flips): the one system watch runs while
// anyone listens.
let stopSystemWatch: (() => void) | null = null;

function subscribe(l: () => void): () => void {
  listeners.add(l);
  stopSystemWatch ??= watchSystemTheme(() => {
    for (const x of listeners) x();
  });
  return () => {
    listeners.delete(l);
    if (listeners.size === 0) {
      stopSystemWatch?.();
      stopSystemWatch = null;
    }
  };
}

/** The reader's choice, live. */
export function useTheme(): Theme {
  return useSyncExternalStore(subscribe, currentTheme, () => "system" as Theme);
}

/** What the page shows now, live: the maps pick their tiles by it. */
export function useResolvedTheme(): Resolved {
  return useSyncExternalStore(subscribe, () => resolveTheme(current), () => "light" as Resolved);
}
